import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { 
  Copy,
  Trash2,
  KeyRound
} from "lucide-react";
import { User as UserType, UserRole } from "@/lib/schema";
import { updateUser } from "@/api/users";
import { getInitials } from "@/lib/userUtils";
import { callAuthenticatedFunction } from "@/lib/authUtils";
import { DeleteUserConfirmationModal } from "./DeleteUserConfirmationModal";
import { RoleChangeConfirmationModal } from "./RoleChangeConfirmationModal";

interface UserDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserType | null;
  currentUser: UserType | null;
  onUserUpdate: () => void;
}

export const UserDetailModal = ({ 
  open, 
  onOpenChange, 
  user, 
  currentUser,
  onUserUpdate 
}: UserDetailModalProps) => {
  const { toast } = useToast();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRoleChangeConfirm, setShowRoleChangeConfirm] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState<{ userId: string; newRole: UserRole } | null>(null);

  const handleCloseModal = () => {
    setResetPassword(""); // Clear any displayed password
    onOpenChange(false);
  };

  const handleRoleUpdate = async (userId: string, newRole: UserRole) => {
    setPendingRoleChange({ userId, newRole });
    setShowRoleChangeConfirm(true);
  };

  const confirmRoleUpdate = async () => {
    if (!pendingRoleChange) return;

    setActionLoading('role-update');
    try {
      await updateUser(pendingRoleChange.userId, { role: pendingRoleChange.newRole });
      toast({
        title: "Role Updated",
        description: "User role has been successfully updated."
      });
      setShowRoleChangeConfirm(false);
      setPendingRoleChange(null);
      onUserUpdate(); // Refresh the user list
    } catch (error) {
      console.error('Error updating role:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: "Failed to update user role. Please try again.",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (userToReset: UserType) => {
    setActionLoading('reset');
    try {
      const result = await callAuthenticatedFunction('resetPassword', { uid: userToReset.id });
      const response = result.data as { message: string; newPassword: string };
      
      // Store the new password to display
      setResetPassword(response.newPassword);
      
      // Update user record to track password reset
      await updateUser(userToReset.id, { 
        passwordResetRequired: true,
        lastPasswordReset: new Date(),
        updatedAt: new Date()
      });
      
      toast({
        title: "Password Reset Successfully",
        description: `New password generated for ${userToReset.firstName} ${userToReset.lastName}.`
      });
      onUserUpdate(); // Refresh to show password reset status
    } catch (error: any) {
      console.error('Error resetting password:', error);
      let errorMessage = "Failed to reset password. Please try again.";
      
      if (error.message && typeof error.message === 'string') {
        try {
          const parsedError = JSON.parse(error.message);
          if (parsedError.message) {
            errorMessage = parsedError.message;
          }
        } catch {
          errorMessage = error.message;
        }
      } else if (error.code && error.message) {
        switch (error.code) {
          case 'functions/not-found':
            errorMessage = "User not found in Firebase Auth.";
            break;
          case 'functions/permission-denied':
            errorMessage = "You don't have permission to reset passwords.";
            break;
          case 'functions/invalid-argument':
            errorMessage = "Invalid user ID provided.";
            break;
          case 'functions/internal':
            errorMessage = "An internal server error occurred. Please try again.";
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }
      
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!user || currentUser?.id === user?.id) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Cannot Delete",
        description: "You cannot delete your own account.",
        variant: "destructive"
      });
      return;
    }

    if (!user.id || user.id.trim() === '') {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Cannot Delete",
        description: "User Firebase UID is missing. Cannot delete user.",
        variant: "destructive"
      });
      return;
    }

    setActionLoading('delete');
    try {
      await callAuthenticatedFunction('deleteUser', { uid: user.id });
      
      toast({
        title: "User Deleted",
        description: `${user.firstName} ${user.lastName} has been deleted from both Firebase Auth and Firestore.`
      });
      
      setShowDeleteConfirm(false);
      handleCloseModal();
      onUserUpdate(); // Refresh the user list
    } catch (error: any) {
      let errorMessage = "Failed to delete user. Please try again.";
      
      if (error.message && typeof error.message === 'string') {
        try {
          const parsedError = JSON.parse(error.message);
          if (parsedError.message) {
            errorMessage = parsedError.message;
          }
        } catch {
          errorMessage = error.message;
        }
      } else if (error.code && error.message) {
        switch (error.code) {
          case 'functions/not-found':
            errorMessage = "User not found in Firebase Auth.";
            break;
          case 'functions/permission-denied':
            errorMessage = "You don't have permission to delete users.";
            break;
          case 'functions/internal':
            errorMessage = "An internal server error occurred. Please try again.";
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }
      
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={handleCloseModal}>
      <DialogContent 
        className="max-w-2xl overflow-y-auto scrollbar-hide dialog-content-wrapper"
        onPointerDownOutside={(e) => {
          // Allow clicks on Radix Select components (which render in portals)
          const target = e.target as HTMLElement;
          if (
            target.closest('[data-radix-select-content]') ||
            target.closest('[data-radix-select-viewport]') ||
            target.closest('[data-radix-select-item]') ||
            target.closest('[data-radix-portal]')
          ) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
          <DialogDescription>
            View and manage user information and permissions.
          </DialogDescription>
        </DialogHeader>
        <style>{`
          /* Force z-index and pointer events for Select components */
          [data-radix-select-content],
          [data-radix-select-viewport],
          [data-radix-select-item],
          [data-radix-portal] {
            z-index: 200 !important;
            pointer-events: auto !important;
          }

          /* Prevent scroll on Select trigger focus */
          .dialog-content-wrapper [data-radix-select-trigger],
          .dialog-content-wrapper [data-radix-select-trigger]:focus,
          .dialog-content-wrapper [data-radix-select-trigger]:focus-visible {
            scroll-margin: 0 !important;
            scroll-padding: 0 !important;
            scroll-behavior: auto !important;
          }
        `}</style>
        
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user.profileImageUrl || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-blue-400 to-blue-600 text-white text-lg">
                {getInitials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-gray-600">{user.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Role</Label>
              <Select
                value={user.role}
                onValueChange={(value) => handleRoleUpdate(user.id, value as UserRole)}
                disabled={currentUser?.id === user?.id || actionLoading !== null}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  className="z-[200]"
                  position="popper"
                  sideOffset={5}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <SelectItem value={UserRole.RECRUITER}>Recruiter</SelectItem>
                  <SelectItem value={UserRole.SUPER_ADMIN}>Super Admin</SelectItem>
                </SelectContent>
              </Select>
              {currentUser?.id === user?.id && (
                <p className="text-xs text-amber-600 mt-1">
                  You cannot change your own role
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Created At</Label>
              <Input
                value={new Date(user.createdAt).toLocaleString()}
                className="mt-1"
                readOnly
              />
            </div>

            <div>
              <Label>Updated At</Label>
              <Input
                value={new Date(user.updatedAt).toLocaleString()}
                className="mt-1"
                readOnly
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Password Reset Required</Label>
              <Input
                value={user.passwordResetRequired !== undefined ? (user.passwordResetRequired ? "Yes" : "No") : "Unknown"}
                className={`mt-1 ${user.passwordResetRequired ? 'text-amber-600' : 'text-green-600'}`}
                readOnly
              />
            </div>

            <div>
              <Label>Onboarding Completed</Label>
              <Input
                value={user.onboardingCompleted !== undefined ? (user.onboardingCompleted ? "Yes" : "No") : "Unknown"}
                className={`mt-1 ${user.onboardingCompleted ? 'text-green-600' : 'text-blue-600'}`}
                readOnly
              />
            </div>
          </div>

          {user.invitedAt && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invited At</Label>
                <Input
                  value={user.invitedAt ? new Date(user.invitedAt).toLocaleString() : "Not available"}
                  className="mt-1"
                  readOnly
                />
              </div>
              
              {user.lastPasswordReset && (
                <div>
                  <Label>Last Password Reset</Label>
                  <Input
                    value={user.lastPasswordReset ? new Date(user.lastPasswordReset).toLocaleString() : "Never"}
                    className="mt-1"
                    readOnly
                  />
                </div>
              )}
            </div>
          )}

          {/* Reset Password Display */}
          {resetPassword && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <Label className="text-green-800 font-semibold">New Password Generated</Label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="text"
                  value={resetPassword}
                  readOnly
                  className="font-mono text-sm bg-white"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(resetPassword);
                    toast({
                      title: "Password Copied",
                      description: "New password copied to clipboard."
                    });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-green-700 mt-1">
                This is the new temporary password. Share it securely with the user.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="border-t pt-6">
            <Label className="text-base font-semibold">Actions</Label>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleResetPassword(user)}
                disabled={actionLoading !== null}
                className="flex items-center gap-2"
              >
                <KeyRound className="h-4 w-4" />
                {actionLoading === 'reset' ? 'Resetting...' : 'Reset Password'}
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteUser}
                disabled={actionLoading !== null || currentUser?.id === user?.id}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>

            {currentUser?.id === user?.id && (
              <p className="text-xs text-amber-600 mt-2">
                You cannot delete your own account
              </p>
            )}
          </div>
        </div>
      </DialogContent>
      
      {/* Delete Confirmation Modal */}
      <DeleteUserConfirmationModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        user={user}
        onConfirm={confirmDeleteUser}
        isLoading={actionLoading === 'delete'}
      />

      {/* Role Change Confirmation Modal */}
      <RoleChangeConfirmationModal
        open={showRoleChangeConfirm}
        onOpenChange={setShowRoleChangeConfirm}
        user={user}
        newRole={pendingRoleChange?.newRole || null}
        onConfirm={confirmRoleUpdate}
        onCancel={() => {
          setShowRoleChangeConfirm(false);
          setPendingRoleChange(null);
        }}
        isLoading={actionLoading === 'role-update'}
      />
    </Dialog>
  );
};
